import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { downloadFile } from '@/shared/utils/downloadFile';
import { downloadIgnoredRows } from '../api/format-specs-admin.api';
import { messageText } from '../lib/message-keys';
import { readProblem } from '../lib/problem';
import {
  useConfirmImport,
  usePreviewImport,
  useRefreshImport,
  useUpdateImport,
} from '../model/useImport';
import type { ImportPreview } from '../model/types';

/* eslint-disable i18next/no-literal-string -- resolution wire values and the accepted file type */
const KEEP_FILE = 'file';
const KEEP_CURRENT = 'current';
const CSV_ACCEPT = '.csv,text/csv';
/* eslint-enable i18next/no-literal-string */

interface Selection {
  files: File[];
  /** Minted per file selection so a retry of the same upload is deduplicated server-side. */
  idempotencyKey: string;
}

const EMPTY: Selection = { files: [], idempotencyKey: '' };

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Upload, review, confirm. Nothing changes until the last step, and the preview says everything
 * the import would do - including each row it could not use and why - before anyone confirms it.
 */
export function ImportDialog({ open, onOpenChange }: ImportDialogProps) {
  const { t } = useTranslation('admin');
  const [selection, setSelection] = useState<Selection>(EMPTY);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const previewImport = usePreviewImport();
  const refreshImport = useRefreshImport();
  const updateImport = useUpdateImport();
  const confirmImport = useConfirmImport();

  useEffect(() => {
    if (open) return;
    setSelection(EMPTY);
    setPreview(null);
  }, [open]);

  const runPreview = async () => {
    try {
      setPreview(await previewImport.mutateAsync(selection));
    } catch {
      notificationService.showError(t('admin.adFormats.errors.generic'));
    }
  };

  const update = async (body: {
    resolutions?: Record<string, 'file' | 'current'>;
    confirmBreaking?: boolean;
  }) => {
    if (!preview) return;
    try {
      setPreview(await updateImport.mutateAsync({ id: preview.id, ...body }));
    } catch {
      notificationService.showError(t('admin.adFormats.errors.generic'));
    }
  };

  const confirm = async () => {
    if (!preview) return;
    try {
      const result = await confirmImport.mutateAsync(preview.id);
      notificationService.showSuccess(
        result.changedRecords === 0
          ? t('admin.adFormats.nothingChanged')
          : t('admin.adFormats.importDialog.imported', { version: result.datasetVersion }),
      );
      onOpenChange(false);
    } catch (error) {
      if (readProblem(error).code === 'preview_stale') {
        notificationService.showError(t('admin.adFormats.importDialog.stale'));
        try {
          setPreview(await refreshImport.mutateAsync(preview.id));
        } catch {
          notificationService.showError(t('admin.adFormats.errors.generic'));
        }
        return;
      }
      notificationService.showError(t('admin.adFormats.errors.generic'));
    }
  };

  const downloadReport = async () => {
    if (!preview) return;

    try {
      await downloadFile({
        blob: await downloadIgnoredRows(preview.id),
        fileName: 'ignored-rows.csv',
      });
    } catch {
      notificationService.showError(t('admin.adFormats.errors.generic'));
    }
  };

  const busy = previewImport.isPending || updateImport.isPending || confirmImport.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin.adFormats.importDialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.adFormats.importDialog.description')}</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <Field>
            <FieldLabel htmlFor="import-files">
              {t('admin.adFormats.importDialog.chooseFiles')}
            </FieldLabel>
            <Input
              id="import-files"
              type="file"
              accept={CSV_ACCEPT}
              multiple
              onChange={(event) =>
                setSelection({
                  files: Array.from(event.target.files ?? []),
                  idempotencyKey: crypto.randomUUID(),
                })
              }
            />
          </Field>
        ) : (
          <div className="flex flex-col gap-md">
            <p className="type-label-md">
              {preview.changes.length === 0
                ? t('admin.adFormats.importDialog.noChanges')
                : t('admin.adFormats.importDialog.changes', { count: preview.changes.length })}
            </p>
            <ul className="flex flex-col gap-2xs text-body-sm text-muted-foreground">
              {Object.entries(preview.counts).map(([file, counts]) => (
                <li key={file}>
                  {t('admin.adFormats.importDialog.fileCounts', { file, ...counts })}
                </li>
              ))}
            </ul>

            {preview.warnings.length > 0 && (
              <Alert variant="default">
                <AlertTitle>{t('admin.adFormats.importDialog.warnings')}</AlertTitle>
                <AlertDescription>
                  <ul>
                    {preview.warnings.map((warning) => (
                      <li key={`${warning.code}:${JSON.stringify(warning.params)}`}>
                        {messageText(t, warning)}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {preview.ignoredCount > 0 && (
              <Alert variant="destructive">
                <AlertTitle>
                  {t('admin.adFormats.importDialog.ignoredTitle', {
                    count: preview.ignoredCount,
                  })}
                </AlertTitle>
                <AlertDescription className="flex flex-col gap-xs">
                  <ul className="max-h-40 overflow-y-auto">
                    {preview.ignored.map((row) => (
                      <li key={`${row.file}:${row.row}:${row.column ?? ''}`}>
                        {t('admin.adFormats.importDialog.ignoredRow', {
                          file: row.file,
                          row: row.row,
                          column: row.column,
                          reason: messageText(t, row),
                        })}
                      </li>
                    ))}
                  </ul>
                  {preview.ignoredTruncated && (
                    <p>
                      {t('admin.adFormats.importDialog.ignoredTruncated', {
                        shown: preview.ignored.length,
                      })}
                    </p>
                  )}
                  <div>
                    <Button variant="outline" size="sm" onClick={() => void downloadReport()}>
                      {t('admin.adFormats.importDialog.downloadIgnored')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {preview.conflicts.length > 0 && (
              <section className="flex flex-col gap-sm">
                <h3 className="type-label-md">
                  {t('admin.adFormats.importDialog.conflictsTitle')}
                </h3>
                {preview.conflicts.map((conflict) => (
                  <fieldset
                    key={conflict.id}
                    className="flex flex-col gap-xs rounded-md border border-border p-sm"
                  >
                    <legend className="font-mono text-body-sm">{conflict.id}</legend>
                    <RadioGroup
                      value={preview.resolutions[conflict.id] ?? ''}
                      onValueChange={(value) =>
                        void update({ resolutions: { [conflict.id]: value as 'file' | 'current' } })
                      }
                    >
                      <div className="flex items-center gap-xs">
                        <RadioGroupItem id={`${conflict.id}-file`} value={KEEP_FILE} />
                        <Label htmlFor={`${conflict.id}-file`}>
                          {t('admin.adFormats.importDialog.keepFile')}
                        </Label>
                      </div>
                      <div className="flex items-center gap-xs">
                        <RadioGroupItem id={`${conflict.id}-current`} value={KEEP_CURRENT} />
                        <Label htmlFor={`${conflict.id}-current`}>
                          {t('admin.adFormats.importDialog.keepCurrent')}
                        </Label>
                      </div>
                    </RadioGroup>
                  </fieldset>
                ))}
              </section>
            )}

            {preview.breakingFormats.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle>{t('admin.adFormats.importDialog.breakingTitle')}</AlertTitle>
                <AlertDescription className="flex flex-col gap-xs">
                  <p className="font-mono">{preview.breakingFormats.join(', ')}</p>
                  <div className="flex items-center gap-xs">
                    <Checkbox
                      id="import-confirm-breaking"
                      checked={preview.confirmBreaking}
                      onCheckedChange={(checked) =>
                        void update({ confirmBreaking: checked === true })
                      }
                    />
                    <Label htmlFor="import-confirm-breaking">
                      {t('admin.adFormats.importDialog.confirmBreaking')}
                    </Label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {preview.issues.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle>{t('admin.adFormats.errors.validationFailed')}</AlertTitle>
                <AlertDescription>
                  <ul>
                    {preview.issues.map((issue) => (
                      <li key={`${issue.entityKey}:${issue.field ?? ''}`}>
                        {messageText(t, issue)}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('admin.adFormats.cancel')}
          </Button>
          {!preview ? (
            <Button
              disabled={selection.files.length === 0 || busy}
              onClick={() => void runPreview()}
            >
              {t('admin.adFormats.importDialog.preview')}
            </Button>
          ) : (
            <Button disabled={!preview.canConfirm || busy} onClick={() => void confirm()}>
              {t('admin.adFormats.importDialog.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
